import fallbackImage from "@assets/images/fallback-game.svg";
import {IconPlayerPlay} from "@tabler/icons-react";
import {useCallback, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Carousel} from "@/components";
import {ImageWithLoadSpinner} from "@/components/image-with-load-spinner";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./ThemeGamesCarousel.module.css";

const CAROUSEL_ITEMS = 20;

const ThemeGamesCarousel = ({title, icon, filterFn, searchTerm}) => {
  const navigate = useNavigate();
  const allGames = useMiniGames(state => state.list);
  const setStoreSearch = useMiniGames(state => state.setSearch);
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const [carouselApi, setCarouselApi] = useState(null);

  const games = useMemo(() => allGames.filter(filterFn), [allGames, filterFn]);

  const handleGameClick = useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleViewAll = useCallback(() => {
    if (authStatus !== "authenticated") {
      handleShowAuthModal();
      return;
    }
    setStoreSearch(searchTerm);
    navigate("/gameslist/all#all-games", {
      state: {
        fromThemeViewAllAt: Date.now(),
      },
    });
  }, [authStatus, handleShowAuthModal, setStoreSearch, searchTerm, navigate]);

  if (games.length === 0) return null;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span
            className={styles.icon}
            aria-hidden="true"
          >
            {icon}
          </span>
          <h2 className={styles.title}>{title}</h2>
          <span className={styles.count}>{games.length}</span>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.viewAll}
            onClick={handleViewAll}
          >
            Ver todo
            <span className={styles.viewAllArrow}>›</span>
          </button>
          {carouselApi && (
            <div className={styles.arrowGroup}>
              <button
                type="button"
                className={styles.arrowBtn}
                onClick={carouselApi.scrollPrev}
                aria-label="Anterior"
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className={styles.arrowBtn}
                onClick={carouselApi.scrollNext}
                aria-label="Siguiente"
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <Carousel
        onInit={setCarouselApi}
        speed={1}
        pauseOnHover
      >
        {Array.from({length: Math.min(CAROUSEL_ITEMS, games.length * 2)}).map((_, idx) => {
          const game = games[idx % games.length];
          return (
            <div
              key={`tg-${game.id}-${idx}`}
              className={styles.card}
              onClick={() => handleGameClick(game.provider, game.id)}
              role="button"
              tabIndex={0}
              aria-label={game.name}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleGameClick(game.provider, game.id);
                }
              }}
            >
              <ImageWithLoadSpinner
                src={game.icon || fallbackImage}
                alt={game.name}
                fallbackSrc={fallbackImage}
                wrapperClassName={styles.cardImg}
                imgClassName={styles.thumbImg}
                objectFit="cover"
                loading="lazy"
                showSpinner={false}
              />
              <div className={styles.gradientOverlay} />
              <div className={styles.playOverlay}>
                <div className={styles.playBtn}>
                  <IconPlayerPlay
                    size={16}
                    fill="#000"
                  />
                </div>
              </div>
              <div className={styles.cardFooter}>
                <span className={styles.gameName}>{game.name}</span>
                <span className={styles.gameProvider}>{game.provider}</span>
              </div>
            </div>
          );
        })}
      </Carousel>
    </section>
  );
};

export default ThemeGamesCarousel;
